import React, { useEffect, useRef } from 'react';
import { KoobCard } from '@koob/ui';
import ChatboxInput from './ChatboxInput';
import { sendMessage } from 'api/trip';
import { useTranslation } from 'react-i18next';
import ChatboxMessage from '../../../components/Hotel/Booking/Edit/Chat/Partials/ChatboxMessage';
import { useError } from '../../../hooks';
import { TripConversationStatusEnum } from '@koob/enums';
import { useTripFilters } from '@koob/trip-designer';

export default function ChatboxTrip({
  messages,
  refetch,
  tripId,
  conversationId,
  conversationTitle,
  conversationStatus,
  secret,
  displayAs,
  showConversationTab,
  conversationReload,
  currentOrganizationId,
}) {
  const messageEndRef = useRef(null);
  const { t } = useTranslation('tripDesigner');
  const { sendErrorSnack } = useError();
  const tripFilters = useTripFilters();
  // A closed conversation is read-only: the API reopens any conversation it
  // receives a message for, so posting here would silently revive it.
  const isClosed = conversationStatus === TripConversationStatusEnum.CLOSED;

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessageTrip = async values => {
    if (isClosed) return;
    if (!values.message || values.message.trim() === '') return;

    try {
      await sendMessage({
        tripId: tripId ?? tripFilters?.tripId,
        conversationId: conversationId,
        body: {
          title: values.title,
          message: values.message,
        },
      });
      refetch();
      conversationReload();
    } catch (error) {
      sendErrorSnack(error);
    }
  };

  return (
    <KoobCard>
      <div className="h-[36rem] overflow-scroll">
        <div className="py-5 px-[6%] flex flex-col space-y-5">
          {showConversationTab === 'list' && (
            <div className="text-sm text-center pb-2 border-b">
              <div className="flex items-center justify-center">
                <div className="font-bold mr-2">{t('conversation.object')}</div>
                {conversationTitle}
              </div>
            </div>
          )}
          {conversationId &&
            messages?.length > 0 &&
            messages?.map(message => (
              <ChatboxMessage
                key={message.id}
                message={message}
                displayAs={displayAs}
                currentOrganizationId={currentOrganizationId}
                dangerouslyHtml
              />
            ))}
          <div ref={messageEndRef} />
        </div>
      </div>

      {showConversationTab === 'list' &&
        (isClosed ? (
          <div className="min-h-20 px-5 py-4 border-t flex items-center justify-center text-sm text-gray-500">
            <i className="fa-regular fa-box-archive mr-2" />
            {t('conversation.closedNotice')}
          </div>
        ) : (
          <ChatboxInput
            tripId={tripId}
            conversationId={conversationId}
            secret={secret}
            refetch={refetch}
            sendMessage={sendMessageTrip}
          />
        ))}
    </KoobCard>
  );
}
